import { ExternalLink, MonitorUp, Search, Wrench } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, useParams, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useVendorPages } from "@/hooks/useVendorPages";
import type { VendorPage } from "@/lib/api";
import { cn } from "@/lib/utils";

/** Tool iframe host — reads tool pages from the vendor page catalog.
 *  Each iframeable tool has its own route ``/tools/:toolId``; the app
 *  sidebar drives selection. Bare ``/tools`` redirects to the last
 *  viewed tool (localStorage) or the first iframeable one.
 *
 *  Iframe state preservation — every tool the operator has visited
 *  stays mounted with ``visibility:hidden`` when not current, so
 *  switching is instant and WebSocket sessions survive. Unvisited
 *  tools aren't mounted until first navigation.
 */

const LS_KEY = "signals_web.tools.current";
const IFRAME_ACCESS_MODES = new Set(["iframe", "scripted_iframe"]);

export default function ToolsPage() {
  const { toolId } = useParams<{ toolId?: string }>();
  const pagesQ = useVendorPages();

  if (pagesQ.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-fg">
        Loading tools…
      </div>
    );
  }
  if (pagesQ.isError) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-red-400">
        Failed to load tools: {(pagesQ.error as Error).message}
      </div>
    );
  }

  const tools = (pagesQ.data ?? []).filter((page) => page.page_type === "tool");
  const iframeable = tools.filter(isIframeTool);

  if (!toolId) {
    return <ToolsDirectory tools={tools} />;
  }

  if (tools.some((tool) => tool.vendor === toolId)) {
    return <ToolsDirectory tools={tools} vendorId={toolId} />;
  }

  const current = iframeable.find((t) => t.id === toolId);
  if (!current) {
    return <Navigate to="/tools" replace />;
  }

  return <ToolsFrameHost currentId={current.id} iframeable={iframeable} />;
}

function ToolsDirectory({
  tools,
  vendorId,
}: {
  tools: VendorPage[];
  vendorId?: string;
}) {
  const [query, setQuery] = useState("");
  const [searchParams] = useSearchParams();
  const vendorFilter = useMemo(
    () => new Set(vendorId ? [vendorId] : csvParam(searchParams, "vendors")),
    [searchParams, vendorId],
  );
  const accessFilter = useMemo(
    () => new Set(csvParam(searchParams, "access")),
    [searchParams],
  );
  const capabilityFilter = useMemo(
    () => new Set(csvParam(searchParams, "capabilities")),
    [searchParams],
  );
  const visibleTools = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return tools.filter((tool) => {
      if (vendorFilter.size > 0 && !vendorFilter.has(tool.vendor)) return false;
      if (accessFilter.size > 0 && !accessFilter.has(tool.access_mode ?? "external")) return false;
      if (
        capabilityFilter.size > 0 &&
        !Array.from(capabilityFilter).every((capability) => hasCapability(tool, capability))
      ) {
        return false;
      }
      if (!needle) return true;
      return [
        tool.id,
        tool.label,
        tool.nav_label,
        tool.vendor_display_name,
        tool.subscription,
        tool.section,
        tool.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [accessFilter, capabilityFilter, query, tools, vendorFilter]);
  const vendors = groupByVendor(visibleTools);
  const activeVendor = vendorId ? tools.find((tool) => tool.vendor === vendorId) : null;
  const activeFilters = filterLabels({
    vendors: vendorFilter,
    access: accessFilter,
    capabilities: capabilityFilter,
  });

  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-border px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-muted-fg">
              <Wrench className="size-4" aria-hidden />
              <span>{vendorId ? activeVendor?.vendor_display_name ?? vendorId : "All vendors"}</span>
            </div>
            <h1 className="mt-1 text-xl font-semibold text-fg">
              {vendorId ? "Vendor tools" : "Tools"}
            </h1>
            <p className="mt-1 text-sm text-muted-fg">
              Vendor tools from the canonical vendor page catalog.
            </p>
            {activeFilters.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {activeFilters.map((filter) => (
                  <Badge key={filter} variant="outline">
                    {filter}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-fg" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search tools"
              className="h-9 w-full rounded-md border border-input bg-bg pl-9 pr-3 text-sm text-fg outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
        </div>
      </header>
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-6">
        {vendors.length === 0 ? (
          <div className="rounded-md border border-border bg-muted/5 p-4 text-sm text-muted-fg">
            No tools match this view.
          </div>
        ) : (
          <div className="space-y-6">
            {vendors.map((vendor) => (
              <section key={vendor.id} className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-semibold text-fg">{vendor.label}</h2>
                  {!vendorId ? (
                    <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
                      <NavLink to={`/tools/${vendor.id}`}>Vendor view</NavLink>
                    </Button>
                  ) : null}
                  <span className="text-xs text-muted-fg">{vendor.tools.length} tools</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {vendor.tools.map((tool) => (
                    <ToolCard key={tool.id} tool={tool} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolCard({ tool }: { tool: VendorPage }) {
  const iframeable = isIframeTool(tool);
  return (
    <article className="flex min-h-36 flex-col rounded-md border border-border bg-muted/5 p-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-medium text-fg">{displayLabel(tool)}</h3>
            <p className="mt-1 truncate text-xs text-muted-fg">
              {tool.subscription.replace(/_/g, " ")}
              {tool.section ? ` · ${tool.section.replace(/_/g, " ")}` : ""}
            </p>
          </div>
          <Badge variant={tool.status === "active" ? "success" : "outline"}>
            {tool.status}
          </Badge>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge variant="secondary">{tool.access_mode ?? "external"}</Badge>
          {tool.capabilities.scrape ? <Badge variant="outline">scrape</Badge> : null}
          {tool.capabilities.warm_page ? <Badge variant="outline">warm</Badge> : null}
          {tool.capabilities.monitor_activity ? <Badge variant="outline">monitor</Badge> : null}
        </div>
        {tool.notes ? (
          <p className="mt-3 line-clamp-2 text-xs text-muted-fg">{tool.notes}</p>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {iframeable ? (
          <Button asChild size="sm" className="h-8 px-2.5 text-xs">
            <NavLink to={`/tools/${tool.id}`}>
              <MonitorUp className="size-3.5" />
              Open
            </NavLink>
          </Button>
        ) : null}
        <Button asChild size="sm" variant="outline" className="h-8 px-2.5 text-xs">
          <a href={tool.url} target="_blank" rel="noreferrer">
            <ExternalLink className="size-3.5" />
            New tab
          </a>
        </Button>
      </div>
    </article>
  );
}

function ToolsFrameHost({
  currentId,
  iframeable,
}: {
  currentId: string;
  iframeable: VendorPage[];
}) {
  const [visited, setVisited] = useState<Set<string>>(() => new Set([currentId]));

  useEffect(() => {
    localStorage.setItem(LS_KEY, currentId);
    setVisited((prev) => {
      if (prev.has(currentId)) return prev;
      const next = new Set(prev);
      next.add(currentId);
      return next;
    });
  }, [currentId]);

  const current = iframeable.find((t) => t.id === currentId)!;

  return (
    <div className="flex h-full flex-col">
      <PanelHeader tool={current} />
      <div className="relative flex-1 bg-white">
        {iframeable
          .filter((t) => visited.has(t.id))
          .map((tool) => (
            <ToolFrame
              key={tool.id}
              tool={tool}
              visible={currentId === tool.id}
            />
          ))}
      </div>
    </div>
  );
}

function ToolFrame({ tool, visible }: { tool: VendorPage; visible: boolean }) {
  const [version, setVersion] = useState(0);
  const missingParams = useMemo(
    () => missingScriptedParams(tool),
    [tool, version],
  );
  const src = missingParams.length === 0 ? effectiveIframeUrl(tool) : "";

  if (missingParams.length > 0) {
    return (
      <ScriptedIframeSetup
        tool={tool}
        missingParams={missingParams}
        visible={visible}
        onSaved={() => setVersion((current) => current + 1)}
      />
    );
  }

  return (
    <iframe
      src={src}
      title={tool.label}
      className={cn("absolute inset-0 h-full w-full", visible ? "visible" : "invisible")}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      referrerPolicy="no-referrer"
    />
  );
}

function ScriptedIframeSetup({
  tool,
  missingParams,
  visible,
  onSaved,
}: {
  tool: VendorPage;
  missingParams: ScriptedIframeParam[];
  visible: boolean;
  onSaved: () => void;
}) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    missingParams.forEach((param) => {
      const value = String(data.get(param.name) ?? "").trim();
      if (value && param.storage_key) {
        localStorage.setItem(param.storage_key, value);
      }
    });
    onSaved();
  }

  return (
    <div
      className={cn(
        "absolute inset-0 flex items-center justify-center bg-bg px-4",
        visible ? "visible" : "invisible",
      )}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-md border border-border bg-card p-4 shadow-sm"
      >
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-fg">{tool.label}</h2>
          <p className="mt-1 text-xs text-muted-fg">
            This vendor tool needs a local launch parameter before it can open.
          </p>
        </div>
        <div className="space-y-3">
          {missingParams.map((param) => (
            <label key={param.name} className="block">
              <span className="mb-1 block text-xs font-medium text-muted-fg">
                {param.label ?? param.name}
              </span>
              <input
                name={param.name}
                type="text"
                required={param.required ?? true}
                className="h-9 w-full rounded-md border border-input bg-bg px-3 text-sm text-fg outline-none focus:ring-1 focus:ring-accent"
              />
            </label>
          ))}
        </div>
        <button
          type="submit"
          className="mt-4 h-9 rounded-md bg-accent px-3 text-sm font-medium text-accent-foreground hover:bg-accent/90"
        >
          Save and Open
        </button>
      </form>
    </div>
  );
}

function PanelHeader({ tool }: { tool: VendorPage }) {
  return (
    <header className="flex items-center gap-2 border-b border-border bg-muted/10 px-4 py-2 text-xs">
      <span className="font-medium text-fg">{tool.label}</span>
      <span className="text-muted-fg/70">·</span>
      <span className="truncate text-muted-fg">
        {tool.vendor_display_name} · {tool.subscription.replace(/_/g, " ")}
      </span>
      {tool.notes ? (
        <span
          className="hidden truncate text-muted-fg/80 sm:inline"
          title={tool.notes}
        >
          · {tool.notes}
        </span>
      ) : null}
      {tool.url ? (
        <a
          href={tool.url}
          target="_blank"
          rel="noreferrer"
          title="Open in new tab"
          className="ml-auto rounded-sm p-1 text-muted-fg hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <ExternalLink className="size-3.5" aria-hidden />
        </a>
      ) : null}
    </header>
  );
}

function effectiveIframeUrl(tool: VendorPage): string {
  if (!tool.iframe_url) return "";
  const config = scriptedConfig(tool);
  const iframeUrl = new URL(tool.iframe_url);
  const url =
    tool.access_mode === "scripted_iframe" && config?.proxy
      ? proxyUrlForTool(tool, iframeUrl)
      : iframeUrl;
  if (url !== iframeUrl) {
    iframeUrl.searchParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
  }
  scriptedParams(tool).forEach((param) => {
    const value = scriptedParamValue(param);
    if (value) {
      url.searchParams.set(param.name, value);
    }
  });
  return url.origin === window.location.origin
    ? `${url.pathname}${url.search}`
    : url.toString();
}

function proxyUrlForTool(tool: VendorPage, iframeUrl: URL): URL {
  const path = iframeUrl.pathname.replace(/^\/+/, "");
  return new URL(`/api/v1/iframe/${tool.id}/${path}`, window.location.origin);
}

function isIframeTool(tool: VendorPage): boolean {
  return IFRAME_ACCESS_MODES.has(tool.access_mode ?? "") && tool.iframe_url !== null;
}

interface ScriptedIframeParam {
  name: string;
  label?: string;
  value?: string;
  storage_key?: string;
  required?: boolean;
}

interface ScriptedIframeConfig {
  proxy?: boolean;
  query_params?: ScriptedIframeParam[];
}

function scriptedParams(tool: VendorPage): ScriptedIframeParam[] {
  const config = scriptedConfig(tool);
  return Array.isArray(config?.query_params) ? config.query_params : [];
}

function scriptedConfig(tool: VendorPage): ScriptedIframeConfig | undefined {
  if (tool.access_mode !== "scripted_iframe") return undefined;
  return tool.config.scripted_iframe as ScriptedIframeConfig | undefined;
}

function missingScriptedParams(tool: VendorPage): ScriptedIframeParam[] {
  return scriptedParams(tool).filter(
    (param) => (param.required ?? true) && !scriptedParamValue(param),
  );
}

function scriptedParamValue(param: ScriptedIframeParam): string | null {
  if (param.value) return param.value;
  if (param.storage_key) {
    return localStorage.getItem(param.storage_key);
  }
  return null;
}

function displayLabel(vendorPage: VendorPage): string {
  return vendorPage.nav_label ?? vendorPage.label;
}

function groupByVendor(tools: VendorPage[]): Array<{
  id: string;
  label: string;
  tools: VendorPage[];
}> {
  const groups = new Map<string, { id: string; label: string; tools: VendorPage[] }>();
  tools.forEach((tool) => {
    const existing = groups.get(tool.vendor);
    if (existing) {
      existing.tools.push(tool);
      return;
    }
    groups.set(tool.vendor, {
      id: tool.vendor,
      label: tool.vendor_display_name || tool.vendor,
      tools: [tool],
    });
  });
  return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function csvParam(searchParams: URLSearchParams, key: string): string[] {
  return searchParams
    .get(key)
    ?.split(",")
    .map((value) => value.trim())
    .filter(Boolean) ?? [];
}

function hasCapability(tool: VendorPage, capability: string): boolean {
  return Boolean(tool.capabilities[capability as keyof VendorPage["capabilities"]]);
}

function filterLabels(filters: Record<string, Set<string>>): string[] {
  return Object.entries(filters).flatMap(([name, values]) =>
    Array.from(values).map((value) => `${name}: ${value}`),
  );
}
